import type { RefObject } from 'react';
import { useEffect, useState, useRef } from 'react';
import * as d3 from 'd3';
import cloud from 'd3-cloud';
import styled from '@emotion/styled';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const MIN_FONT_SIZE = 16;
const MAX_FONT_SIZE = 180;
const COLORS = [
  '#CDCDCD',
  '#AFC6D5',
  '#91BEDD',
  '#B9C885',
  '#E0D22C',
  '#EAA038',
  '#F46E43',
  '#C23722',
  '#B90000',
];

interface CloudWord extends cloud.Word {
  color: string;
  text: string;
  size: number;
}

// eslint-disable-next-line func-params-args/func-params
function fontSize(minSize: number, maxSize: number, value: number, maxValue: number): number {
  return Math.max((Math.sqrt(value) / Math.sqrt(maxValue)) * maxSize, minSize);
}

function redraw(layoutSize: [number, number], ref: RefObject<SVGSVGElement>, data: CloudWord[]) {
  const svg = d3.select(ref.current);

  svg.selectAll('*').remove();

  svg
    .attr('width', layoutSize[0])
    .attr('height', layoutSize[1])
    .append('g')
    .attr('transform', `translate(${layoutSize[0] / 2},${layoutSize[1] / 2})`)
    .selectAll('text')
    .data(data)
    .enter()
    .append('text')
    .style('font-size', (d) => `${d.size}px`)
    .style('font-family', 'Impact')
    .style('fill', (d) => d.color)
    .attr('text-anchor', 'middle')
    .attr('transform', (d) => `translate(${[d.x, d.y]})rotate(${d.rotate})`)
    .text((d) => d.text);
}

interface WordCloudProps {
  words: [string, number][];
  width: number;
  height: number;
  canvas?: () => HTMLCanvasElement;
}

export function WordCloud(props: WordCloudProps): JSX.Element | null {
  const { words, width, height, canvas } = props;

  const ref = useRef<SVGSVGElement>(null);
  const [maxFontSize, setMaxFontSize] = useState(MAX_FONT_SIZE);

  useEffect(() => {
    const maxFrequency = Math.max(...words.map(([, freq]) => freq));

    const layout = cloud<CloudWord>()
      .size([width, height])
      .random(() => 0.5)
      .words(
        words.map(([text, frequency]) => {
          const index = Math.round((frequency / maxFrequency) * (COLORS.length - 1));
          const size = fontSize(MIN_FONT_SIZE, maxFontSize, frequency, maxFrequency);
          return { text, size, color: COLORS[index] };
        }),
      )
      .padding(2)
      .rotate(() => 0)
      .font('Impact')
      .fontSize((d: CloudWord) => d.size);

    if (canvas) {
      layout.canvas(canvas);
    }

    layout.on('end', (data) => {
      if (data.length < words.length && maxFontSize > MIN_FONT_SIZE) {
        setMaxFontSize((s) => Math.round(s * 0.9));
      } else {
        redraw(layout.size(), ref, data);
      }
    });

    layout.start();

    return () => {
      layout.stop();
    };
  }, [words, width, height, canvas, maxFontSize]);

  return (
    <Wrapper data-testid="word-cloud">
      <svg ref={ref} />
    </Wrapper>
  );
}
